import { useRef, useState } from "react";
import type { userDataType } from "../types";
import {
  PagesClientSearchFiltersContent,
  PagesClientSearchFiltersSlideOut
} from "./filters";
import { AccountSearchViewTop } from "./Top";
import { AccountSearchViewBottom } from "./Bottom";
import styled from "styled-components";
import type { dealerSearchIncoming } from "@dealer-center/ddc-pages-account-search-interface";
import { useLocalStorageKeys } from "@dealer-center/core-interfaces-components";
import {
  Button,
  useLocalStorageHook,
  useLocalStorageReducer
} from "@dealer-center/core-components";
import { PagesClientSearchBulk } from "./Bottom/Tabs/Results/bulk";
import { TabOverflowDropdown } from "./Bottom/Tabs/TabOverflowDropdown";
import { AccountSearchViewHeaders } from "./Header";
import { useWindowDimensions } from "@dealer-center/core-custom-components";
import { CreateAccountModal } from "../modals";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";
import { useGetUser } from "@dealer-center/ddc-api-user-fetch";
import { useGetUserPermissions } from "@dealer-center/ddc-pages-account-search-fetch";
import { hasDDCMembership } from "@dealer-center/ddc-pages-account-search-interface";
export interface AccountSearchViewInterface {
  userData: userDataType;
}
const StyledTopArea = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
const StyledAccountSearchView = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  overflow: visible;
`;
const StyledAccountSearchViewMainPage = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 300px;
  flex-grow: 1;
`;
const StyledPagesClientSearchFiltersArea = styled.div`
  width: 300px;
  min-width: 300px;
  overflow: visible;
  padding-right: 8px;
`;
const StyledTopAreaRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const AccountSearchView = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { "cis.account-creation.enable": isAccountCreationEnabled } =
    useCC9Flags();
  const { data: userEntity } = useGetUser();

  const { data: permissions = [] } = useGetUserPermissions(
    !!userEntity?.internal
  );

  const hasPermission = hasDDCMembership(permissions);

  const canCreateAccount =
    isAccountCreationEnabled && !!userEntity?.internal && hasPermission;
  const openCreateModal = () => setIsCreateModalOpen(true);
  const closeCreateModal = () => setIsCreateModalOpen(false);

  const accountSearchFavoritesPersisted =
    useLocalStorageReducer<dealerSearchIncoming>(
      useLocalStorageKeys.ddcAccountSearchFavorites,
      []
    );
  const [accountSearchFavoritesEnable, setAccountSearchFavoritesEnable] =
    useLocalStorageHook<boolean>(
      useLocalStorageKeys.ddcAccountSearchFavoritesEnable,
      false
    );
  const [accountSearchHistoryEnable, setAccountSearchHistoryEnable] =
    useLocalStorageHook<boolean>(
      useLocalStorageKeys.ddcAccountSearchHistoryEnable,
      false
    );
  const [pinFilters, setPinFilters] = useLocalStorageHook<boolean>(
    useLocalStorageKeys.ddcAccountSearchPinFilters,
    true
  );
  const { isLgOrGreater } = useWindowDimensions();

  const [, dispatchAccountSearchFavorites] = accountSearchFavoritesPersisted;

  const [showFilters, setShowFilters] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  return (
    <>
      <StyledTopArea>
        <AccountSearchViewHeaders />
        <StyledTopAreaRight>
          {canCreateAccount && (
            <Button
              analyticsConfiguration={{
                eventName: "Create Account Button Clicked"
              }}
              buttonStyle="primary"
              onClick={openCreateModal}
            >
              Create Account
            </Button>
          )}
          <TabOverflowDropdown
            accountSearchFavoritesEnable={accountSearchFavoritesEnable}
            accountSearchHistoryEnable={accountSearchHistoryEnable}
            setAccountSearchFavoritesEnable={setAccountSearchFavoritesEnable}
            setAccountSearchHistoryEnable={setAccountSearchHistoryEnable}
          />
          <PagesClientSearchBulk />
        </StyledTopAreaRight>
      </StyledTopArea>
      {isCreateModalOpen && (
        <CreateAccountModal close={closeCreateModal} show={isCreateModalOpen} />
      )}
      <StyledAccountSearchView ref={ref}>
        {isLgOrGreater && pinFilters ? (
          <StyledPagesClientSearchFiltersArea>
            <PagesClientSearchFiltersContent
              dispatchAccountSearchFavorites={dispatchAccountSearchFavorites}
              isDesktop={isLgOrGreater}
              pinFilters={pinFilters}
              setPinFilters={setPinFilters}
            />
          </StyledPagesClientSearchFiltersArea>
        ) : (
          <PagesClientSearchFiltersSlideOut
            dispatchAccountSearchFavorites={dispatchAccountSearchFavorites}
            isDesktop={isLgOrGreater}
            pinFilters={pinFilters}
            setPinFilters={setPinFilters}
            setShowFilters={setShowFilters}
            showFilters={showFilters}
          />
        )}
        <StyledAccountSearchViewMainPage>
          <AccountSearchViewTop
            setShowFilters={
              isLgOrGreater && pinFilters ? undefined : setShowFilters
            }
          />
          <AccountSearchViewBottom
            accountSearchFavoritesPersisted={accountSearchFavoritesPersisted}
            shouldShowFullWidthTable={isLgOrGreater && !pinFilters}
            tabsEnabled={{
              historyEnabled: accountSearchHistoryEnable,
              favoritesEnabled: accountSearchFavoritesEnable,
              setHistoryEnabled: setAccountSearchHistoryEnable,
              setFavoritesEnabled: setAccountSearchFavoritesEnable
            }}
          />
        </StyledAccountSearchViewMainPage>
      </StyledAccountSearchView>
    </>
  );
};
